<template>
  <AppForm
    class="flex flex-col gap-5"
    :problem="problem"
    @submit="handleSubmit"
  >
    <!-- Topic radios -->
    <fieldset>
      <legend
        class="block dark:text-content-level-400 font-semibold mb-3 text-content-level-500 text-sm tracking-[0.12em] uppercase"
      >
        {{ $t('commerce.menuSupportForm.whatDoYouNeedHelpWith') }}
      </legend>

      <FormField
        name="topic"
        label=""
        :error="getFieldError('topic')"
        silent
      >
        <template #control>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="topicOption in topics"
              :key="topicOption.value"
              class="cursor-pointer"
            >
              <span class="inline-flex relative">
                <input
                  type="radio"
                  name="supportTopic"
                  :value="topicOption.value"
                  :checked="topic === topicOption.value"
                  class="peer sr-only"
                  :data-topic="topicOption.value"
                  @change="selectTopicFromEvent"
                />

                <span
                  class="bg-surface-level-900 border border-border-level-700 dark:bg-surface-level-950 dark:border-border-level-700 dark:text-content-level-200 font-semibold inline-flex items-center min-h-8 peer-checked:bg-primary/15 peer-checked:border-primary peer-checked:text-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-hover:border-border-level-500 px-3 py-1.5 rounded-full text-[13px] text-content-level-200 transition"
                >
                  {{ topicOption.label }}
                </span>
              </span>
            </label>
          </div>
        </template>
      </FormField>
    </fieldset>

    <!-- Name -->
    <div>
      <TextField
        v-model="name"
        :label="$t('commerce.menuSupportForm.name')"
        type="text"
        name="name"
        required
        :placeholder="$t('common.menuSupport.yourName')"
        :error="getFieldError('name')"
      />

      <p class="dark:text-content-level-500 mt-1 text-content-level-400 text-xs">
        {{
          $t('commerce.menuSupportForm.fieldCharacterCount', {
            count: name.length,
            max: MENU_SUPPORT_NAME_MAX
          })
        }}
      </p>
    </div>

    <!-- Email -->
    <div>
      <TextField
        v-model="email"
        :label="$t('commerce.menuSupportForm.email')"
        name="email"
        required
        :placeholder="$t('common.menuSupport.emailExample')"
        :error="getFieldError('email')"
      />

      <p class="dark:text-content-level-500 mt-1 text-content-level-400 text-xs">
        {{
          $t('commerce.menuSupportForm.fieldCharacterCount', {
            count: email.length,
            max: MENU_SUPPORT_EMAIL_MAX
          })
        }}
      </p>
    </div>

    <!-- Phone -->
    <PhoneInput
      id="support-phone"
      v-model="phone"
      :label="$t('commerce.menuSupportForm.phoneNumber')"
      :placeholder="$t('common.menuSupport.phoneExample')"
      :required="preferredContactMethod === PreferredContactMethod.PHONE"
      v-bind="getFieldError('phone') ? { error: getFieldError('phone') } : {}"
    />

    <!-- Preferred Contact Method -->
    <fieldset>
      <legend class="block dark:text-content-level-300 font-medium text-content-level-700 text-sm">
        <I18nT
          scope="global"
          keypath="commerce.menuSupportForm.preferredContactMethodRequired"
          ><template #requiredIndicator><span class="text-danger-500">*</span></template></I18nT
        >
      </legend>

      <FormField
        name="preferred_contact_method"
        label=""
        :error="getFieldError('preferred_contact_method')"
        silent
      >
        <template #control>
          <div
            class="bg-surface-level-900 border border-border-level-800 dark:bg-surface-level-950 dark:border-border-level-800 gap-1 grid grid-cols-2 mt-3 p-0.5 rounded-xl shadow-inner"
          >
            <label class="cursor-pointer">
              <span class="flex relative">
                <input
                  type="radio"
                  name="preferredContactMethod"
                  value="email"
                  :checked="preferredContactMethod === PreferredContactMethod.EMAIL"
                  class="peer sr-only"
                  @change="selectEmailContact"
                />

                <span
                  class="flex font-semibold gap-1.5 items-center justify-center min-h-9 peer-checked:bg-primary peer-checked:text-content-level-950 peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-hover:text-content-level-200 px-2 rounded-lg text-[13px] text-content-level-400 transition w-full"
                >
                  <Mail class="h-3.5 w-3.5" />

                  <span>
                    {{ $t('commerce.menuSupportForm.email') }}
                  </span>
                </span>
              </span>
            </label>

            <label class="cursor-pointer">
              <span class="flex relative">
                <input
                  type="radio"
                  name="preferredContactMethod"
                  value="phone"
                  :checked="preferredContactMethod === PreferredContactMethod.PHONE"
                  class="peer sr-only"
                  @change="selectPhoneContact"
                />

                <span
                  class="flex font-semibold gap-1.5 items-center justify-center min-h-9 peer-checked:bg-primary peer-checked:text-content-level-950 peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-hover:text-content-level-200 px-2 rounded-lg text-[13px] text-content-level-400 transition w-full"
                >
                  <Phone class="h-3.5 w-3.5" />

                  <span>
                    {{ $t('commerce.menuSupportForm.phone') }}
                  </span>
                </span>
              </span>
            </label>
          </div>
        </template>
      </FormField>
    </fieldset>

    <!-- Subject -->
    <div>
      <TextField
        v-model="subject"
        :label="$t('commerce.menuSupportForm.subject')"
        type="text"
        name="subject"
        required
        :placeholder="$t('common.menuSupport.topicPlaceholder')"
        :error="getFieldError('subject')"
      />

      <p class="dark:text-content-level-500 mt-1 text-content-level-400 text-xs">
        {{
          $t('commerce.menuSupportForm.fieldCharacterCount', {
            count: subject.length,
            max: MENU_SUPPORT_SUBJECT_MAX
          })
        }}
      </p>
    </div>

    <!-- Message -->
    <div>
      <TextareaField
        v-model="message"
        :label="$t('commerce.menuSupportForm.message')"
        name="message"
        required
        :rows="4"
        :placeholder="$t('common.menuSupport.descriptionPlaceholder')"
        :error="getFieldError('message')"
      />

      <p class="dark:text-content-level-500 mt-1 text-content-level-400 text-xs">
        {{
          $t('commerce.menuSupportForm.fieldCharacterCount', {
            count: message.length,
            max: MENU_SUPPORT_MESSAGE_MAX
          })
        }}
      </p>
    </div>

    <!-- Success display -->
    <div
      v-if="submitSuccess"
      role="status"
      aria-live="polite"
      class="bg-success-50 dark:bg-success-900/20 dark:text-success-400 p-3 rounded-lg text-sm text-success-700"
    >
      {{ $t('commerce.menuSupportForm.yourSupportRequestHasBeenSubmittedWell') }}
    </div>

    <!-- Submit button -->
    <button
      type="submit"
      :disabled="isSubmitting"
      class="cursor-pointer font-semibold px-4 py-2.5 rounded-lg text-sm transition w-full"
      :class="[
        isSubmitting
          ? 'bg-surface-level-200 text-content-level-500 cursor-not-allowed dark:bg-surface-level-700 dark:text-content-level-400'
          : 'bg-primary text-primary-content hover:bg-primary/90'
      ]"
    >
      <span
        v-if="isSubmitting"
        class="gap-2 inline-flex items-center"
      >
        <svg
          class="animate-spin h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />

          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        {{ $t('commerce.menuSupportForm.submitting') }}
      </span>

      <span v-else>
        {{ $t('commerce.menuSupportForm.submitRequest') }}
      </span>
    </button>
  </AppForm>
</template>

<script setup lang="ts">
import { Mail, Phone } from '@lucide/vue'
import { PreferredContactMethod } from '~/enums'
import { MENU_SUPPORT_NAME_MAX } from '~/services/api/contracts/bounds/MENU_SUPPORT_NAME_MAX'
import { MENU_SUPPORT_EMAIL_MAX } from '~/services/api/contracts/bounds/MENU_SUPPORT_EMAIL_MAX'
import { MENU_SUPPORT_SUBJECT_MAX } from '~/services/api/contracts/bounds/MENU_SUPPORT_SUBJECT_MAX'
import { MENU_SUPPORT_MESSAGE_MAX } from '~/services/api/contracts/bounds/MENU_SUPPORT_MESSAGE_MAX'
import PhoneInput from '~/components/forms/inputs/PhoneInput.vue'
import TextareaField from '~/design-system/molecules/TextareaField.molecule.vue'
import TextField from '~/design-system/molecules/TextField.molecule.vue'
import FormField from '~/design-system/molecules/FormField/FormField.molecule.vue'
import AppForm from '~/design-system/molecules/AppForm.molecule.vue'
import type { MenuSupportRequest } from '~/services/api/generated'
import { useMenuSupportForm } from '~/composables/useMenuSupportForm'

const emit = defineEmits<{
  sendRequested: [payload: MenuSupportRequest]
}>()

const {
  email,
  getFieldError,
  handleSubmit,
  isSubmitting,
  message,
  name,
  phone,
  preferredContactMethod,
  problem,
  selectEmailContact,
  selectPhoneContact,
  selectTopic,
  subject,
  submitSuccess,
  topic,
  topics
} = useMenuSupportForm((request) => {
  emit('sendRequested', request)
})

const selectTopicFromEvent = (event: Event) => {
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  selectTopic(target.dataset['topic'])
}
</script>
